import { useCallback, useEffect, useRef, useState } from "react";
import {
  getComplianceFrameworksApi,
  getComplianceControlsApi,
  patchComplianceControlApi,
} from "../../../services/axios/AuthApis/ComplianceOverviewApis";

export function useFrameworks(customerId) {
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getComplianceFrameworksApi(customerId);
      setFrameworks(data?.results || []);
    } catch (e) {
      console.error("ComplianceOverview: failed to load frameworks", e?.response?.data || e);
      setError(e?.response?.data?.message || "Failed to load frameworks.");
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { reload(); }, [reload]);

  return { frameworks, loading, error, reload };
}

// Debounces `search` by 2s before it feeds into the controls query -
// everything else (framework/status/owner/category/flag/page) applies
// immediately.
export function useControls(customerId, filters) {
  const [data, setData] = useState({ results: [], count: 0, total_pages: 1, tab_counts: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search || "");
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(filters.search || ""), 2000);
    return () => clearTimeout(debounceRef.current);
  }, [filters.search]);

  const effectiveFilters = { ...filters, search: debouncedSearch };
  const key = JSON.stringify(effectiveFilters);

  const reload = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (effectiveFilters.framework) params.framework = effectiveFilters.framework;
      if (effectiveFilters.status) params.status = effectiveFilters.status;
      if (effectiveFilters.owner) params.owner = effectiveFilters.owner;
      if (effectiveFilters.category) params.category = effectiveFilters.category;
      if (effectiveFilters.search) params.search = effectiveFilters.search;
      if (effectiveFilters.flag) params.flag = effectiveFilters.flag;
      params.page = effectiveFilters.page || 1;
      params.page_size = effectiveFilters.pageSize || 12;

      const res = await getComplianceControlsApi(customerId, params);
      setData(res || { results: [], count: 0, total_pages: 1, tab_counts: {} });
    } catch (e) {
      console.error("ComplianceOverview: failed to load controls", e?.response?.data || e);
      setError(e?.response?.data?.message || "Failed to load controls.");
    } finally {
      setLoading(false);
    }
  }, [customerId, key]); // eslint intentionally tracks `key`, not the raw `filters` object

  useEffect(() => { reload(); }, [reload]);

  return { ...data, loading, error, reload };
}

export function usePatchControl(customerId) {
  const [pending, setPending] = useState(false);
  const patch = useCallback(async (controlId, body) => {
    setPending(true);
    try {
      return await patchComplianceControlApi(controlId, customerId, body);
    } finally {
      setPending(false);
    }
  }, [customerId]);
  return { patch, pending };
}
